import { useEffect, useState } from 'react';

import { CirclePlus, GripVerticalIcon, Trash2 } from 'lucide-react';
import { Reorder, useDragControls, useMotionValue } from 'motion/react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { StatusBadge, StatusBadgeType } from '@/modules/common';
import { EntityAvatar } from '@/modules/common/components/molecules/avatar/entity-avatar';
import { getTimeOffStatusText } from '@/modules/common/components/molecules/badge/status-configs/time-off-status-config';
import {
  DropdownSearchItem,
  DropdownWithSearchAndPagination,
} from '@/modules/common/components/molecules/selectors/dropdown';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';
import { formatDisplayDate } from '@/utils/date-formatters';

import {
  ApprovalStatus,
  ApprovalStepStatus,
  TimeOffRequestApprover,
} from '../../types/time-off-request';
import { TimeOffStatus } from '../../types/time-off-status';

/** Approver decisions reuse the time-off status badge (REJECTED maps to DECLINED). */
const APPROVAL_TO_TIME_OFF_STATUS: Record<ApprovalStatus, TimeOffStatus> = {
  [ApprovalStatus.Pending]: TimeOffStatus.Pending,
  [ApprovalStatus.Approved]: TimeOffStatus.Approved,
  [ApprovalStatus.Rejected]: TimeOffStatus.Declined,
};

/** A row in the editable (create-mode) approvers table. */
export interface EditableApprover {
  id: number;
  name: string;
  profileImageUrl?: string | null;
  /** True when this approver came from the suggested-approvers prefill. */
  isSuggested?: boolean;
}

/**
 * Fixed drag-row height (`py-4` cell padding + the `xs` avatar), used to clamp
 * the drag to the list bounds — see `DraggableApproverRow`.
 */
const APPROVER_REORDER_ROW_HEIGHT_PX = 56;

interface ApproverNameCellProps {
  name: string;
  profileImageUrl?: string | null;
}

const ApproverNameCell = ({ name, profileImageUrl }: ApproverNameCellProps) => (
  <div className="flex items-center gap-2">
    <EntityAvatar
      image={profileImageUrl}
      name={name}
      size="xs"
      shouldDownscale={false}
      withBorder
    />
    <span className="text-sm font-medium">{name}</span>
  </div>
);

interface TimeOffApproversTableProps {
  /** Read/approve mode: the request's approvers with their decisions. */
  approvers?: TimeOffRequestApprover[];
  /** Create mode: the picked approvers (no decisions yet). */
  editableApprovers?: EditableApprover[];
  /** When true, renders the "Add approver" row + per-row remove (no status column). */
  isEditable?: boolean;
  /** Create mode: loads candidate approvers for the add dropdown. */
  loadApprovers?: (
    searchString?: string,
    cursor?: number | null
  ) => Promise<PaginatedResponse<DropdownSearchItem>>;
  onAddApprover?: (item: DropdownSearchItem) => void;
  onRemoveApprover?: (id: number) => void;
  /** Create mode: render skeleton rows while suggested approvers prefill loads. */
  isLoadingSuggested?: boolean;
  /**
   * Whether the editable rows may be drag-reordered right now (more than one
   * approver, and — in edit mode — none of them has decided yet). When false the
   * rows fall back to today's static rendering (no grip handle, no drag).
   */
  canReorderApprovers?: boolean;
  /** Fires once, on drag end, with the full new approver order. */
  onReorderApprovers?: (orderedApprovers: EditableApprover[]) => void;
}

/**
 * One draggable approver row. The drag `y` is a motion value we own and clamp
 * in `onDrag` to the list bounds (row index × row height) — same mechanics as
 * `DraggableColumnRow` in `reorderable-column-selector.tsx`: deterministic and,
 * unlike `dragConstraints`, never fights `Reorder`'s layout projection.
 */
function DraggableApproverRow({
  approver,
  index,
  rowCount,
  showInfoColumn,
  suggestedLabel,
  dragHandleLabel,
  removeLabel,
  onRemoveApprover,
  onCommit,
}: {
  approver: EditableApprover;
  index: number;
  rowCount: number;
  showInfoColumn: boolean;
  suggestedLabel: string;
  dragHandleLabel: string;
  removeLabel: string;
  onRemoveApprover?: (id: number) => void;
  onCommit: () => void;
}) {
  const dragControls = useDragControls();
  const y = useMotionValue(0);

  const minY = -index * APPROVER_REORDER_ROW_HEIGHT_PX;
  const maxY = (rowCount - 1 - index) * APPROVER_REORDER_ROW_HEIGHT_PX;

  return (
    <Reorder.Item
      as="tr"
      value={approver.id}
      dragListener={false}
      dragControls={dragControls}
      style={{ y }}
      onDrag={() => {
        const current = y.get();
        if (current < minY) y.set(minY);
        else if (current > maxY) y.set(maxY);
      }}
      onDragEnd={onCommit}
      transition={{ type: 'spring', stiffness: 600, damping: 40 }}
      className="relative bg-background"
      whileDrag={{ scale: 1.01, zIndex: 1, boxShadow: '0 4px 12px hsl(var(--border))' }}
    >
      <TableCell className="w-8">
        <button
          type="button"
          aria-label={dragHandleLabel}
          // Pointer-down on the handle hands the gesture to the reorder controls.
          onPointerDown={event => {
            event.stopPropagation();
            dragControls.start(event);
          }}
          className="flex h-6 w-6 flex-shrink-0 cursor-grab touch-none items-center justify-center text-muted-foreground active:cursor-grabbing"
        >
          <GripVerticalIcon className="h-4 w-4" />
        </button>
      </TableCell>
      <TableCell>
        <ApproverNameCell name={approver.name} profileImageUrl={approver.profileImageUrl} />
      </TableCell>
      {showInfoColumn && (
        <TableCell>
          {approver.isSuggested && (
            <Badge className="border-transparent bg-blue-500/15 text-blue-600 hover:bg-blue-500/15 dark:text-blue-400">
              {suggestedLabel}
            </Badge>
          )}
        </TableCell>
      )}
      <TableCell className="w-16 text-right">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-destructive"
          aria-label={removeLabel}
          onClick={() => onRemoveApprover?.(approver.id)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </TableCell>
    </Reorder.Item>
  );
}

/**
 * Approvers as a table (replacing the old chip multiselect).
 *
 * Read/approve modes show each approver's status (as a badge), note and decision
 * date. Create mode shows the picked approvers with a remove action and an "Add
 * approver" dropdown row; the "Info" column (suggested badge) is rendered only
 * when at least one approver was suggested.
 */
export const TimeOffApproversTable = ({
  approvers,
  editableApprovers,
  isEditable = false,
  loadApprovers,
  onAddApprover,
  onRemoveApprover,
  isLoadingSuggested = false,
  canReorderApprovers = false,
  onReorderApprovers,
}: TimeOffApproversTableProps) => {
  const { t } = useTranslation(['authenticated.timeOff', 'common']);

  const hasRows = isEditable ? (editableApprovers?.length ?? 0) > 0 : (approvers?.length ?? 0) > 0;
  // The Info column is only useful when some approvers are suggested.
  const showInfoColumn = isEditable && (editableApprovers?.some(a => a.isSuggested) ?? false);
  // Guarded by the caller's `canReorderApprovers` (more than one approver, and —
  // in edit mode — none has decided yet); also requires actual rows to drag.
  const isReorderable = isEditable && canReorderApprovers && (editableApprovers?.length ?? 0) > 0;

  // Editable column count: approver (+ optional info) + action. Display: 4.
  const columnCount = isEditable ? (showInfoColumn ? 3 : 2) : 4;
  // Add-row spans the grip-handle column too when reordering is active.
  const addRowColumnCount = isReorderable ? columnCount + 1 : columnCount;

  // Local draft order the Reorder.Group animates against during a drag;
  // `onReorderApprovers` fires once on drag end (mirrors `ReorderableColumnSelector`)
  // so the parent form doesn't re-render every frame.
  const [draftApprovers, setDraftApprovers] = useState<EditableApprover[]>(editableApprovers ?? []);
  const approverIdsFingerprint = (editableApprovers ?? []).map(approver => approver.id).join(',');
  useEffect(() => {
    setDraftApprovers(editableApprovers ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approverIdsFingerprint]);

  const handleDraftReorder = (orderedIds: number[]) => {
    const approverById = new Map(draftApprovers.map(approver => [approver.id, approver]));
    setDraftApprovers(
      orderedIds
        .map(id => approverById.get(id))
        .filter((approver): approver is EditableApprover => approver !== undefined)
    );
  };

  const commitReorder = () => onReorderApprovers?.(draftApprovers);

  const renderAddRow = () =>
    isEditable && loadApprovers ? (
      <TableRow className="hover:bg-transparent">
        <TableCell colSpan={addRowColumnCount} className="py-3">
          <DropdownWithSearchAndPagination
            onSelectItem={item => onAddApprover?.(item)}
            loadItems={loadApprovers}
            searchPlaceholder={t('details.approvers.searchPlaceholder')}
            emptyMessage={t('details.approvers.noResults')}
            loadingMessage={t('details.approvers.loading')}
            excludedItemsCount={editableApprovers?.length ?? 0}
            doShowAvatar
          >
            <CirclePlus className="h-4 w-4" />
            {t('details.approvers.addApprover')}
          </DropdownWithSearchAndPagination>
        </TableCell>
      </TableRow>
    ) : null;

  return (
    <div className="overflow-hidden rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            {isReorderable && <TableHead className="w-8" />}
            <TableHead>{t('details.approvers.columns.name')}</TableHead>
            {isEditable ? (
              <>
                {showInfoColumn && <TableHead>{t('details.approvers.columns.info')}</TableHead>}
                <TableHead className="w-16 text-right">
                  {t('details.approvers.columns.action')}
                </TableHead>
              </>
            ) : (
              <>
                <TableHead>{t('details.approvers.columns.status')}</TableHead>
                <TableHead>{t('details.approvers.columns.note')}</TableHead>
                <TableHead>{t('details.approvers.columns.decidedAt')}</TableHead>
              </>
            )}
          </TableRow>
        </TableHeader>
        {isReorderable ? (
          <>
            {/* Drag-to-reorder rows. Only reachable when `canReorderApprovers` is
                true (more than one approver, none decided yet in edit mode), so
                the read-only/locked paths below stay completely untouched. */}
            <Reorder.Group
              as="tbody"
              axis="y"
              values={draftApprovers.map(approver => approver.id)}
              onReorder={handleDraftReorder}
              className="[&_tr:last-child]:border-0"
            >
              {draftApprovers.map((approver, index) => (
                <DraggableApproverRow
                  key={approver.id}
                  approver={approver}
                  index={index}
                  rowCount={draftApprovers.length}
                  showInfoColumn={showInfoColumn}
                  suggestedLabel={t('details.approvers.suggested')}
                  dragHandleLabel={t('details.approvers.dragHandle')}
                  removeLabel={t('details.approvers.remove')}
                  onRemoveApprover={onRemoveApprover}
                  onCommit={commitReorder}
                />
              ))}
            </Reorder.Group>
            <TableBody>{renderAddRow()}</TableBody>
          </>
        ) : (
          <TableBody>
            {/* Empty state — hidden while loading (skeleton rows stand in for it). */}
            {!hasRows && !isLoadingSuggested && (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={columnCount} className="py-4 text-sm text-muted-foreground">
                  {t('details.approvers.empty')}
                </TableCell>
              </TableRow>
            )}

            {/* Suggested-approvers loading skeletons (the add row below stays usable). */}
            {isEditable &&
              isLoadingSuggested &&
              Array.from({ length: 2 }).map((_, index) => (
                <TableRow key={`approver-skeleton-${index}`} className="hover:bg-transparent">
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-6 w-6 rounded-full" />
                      <Skeleton className="h-4 w-32" />
                    </div>
                  </TableCell>
                  {showInfoColumn && (
                    <TableCell>
                      <Skeleton className="h-5 w-20 rounded-full" />
                    </TableCell>
                  )}
                  <TableCell className="w-16 text-right">
                    <Skeleton className="ml-auto h-7 w-7 rounded-md" />
                  </TableCell>
                </TableRow>
              ))}

            {/* Read / approve: approver decisions */}
            {!isEditable &&
              approvers?.map(approver => {
                const status = APPROVAL_TO_TIME_OFF_STATUS[approver.status];
                // A still-pending row distinguishes "their turn now" from "queued behind an
                // earlier approver" instead of both reading as the generic "Pending" label.
                const label =
                  approver.stepStatus === ApprovalStepStatus.AwaitsResponse
                    ? t('details.approvers.stepStatus.awaitsResponse')
                    : approver.stepStatus === ApprovalStepStatus.Blocked
                      ? t('details.approvers.stepStatus.blocked')
                      : getTimeOffStatusText(status);
                return (
                  <TableRow key={approver.user.id} className="hover:bg-transparent">
                    <TableCell>
                      <ApproverNameCell
                        name={approver.user.name}
                        profileImageUrl={approver.user.profileImageUrl}
                      />
                    </TableCell>
                    <TableCell>
                      <StatusBadge type={StatusBadgeType.TimeOff} status={status} label={label} />
                    </TableCell>
                    <TableCell className="text-sm">
                      {approver.comment || t('common:table.noValue')}
                    </TableCell>
                    <TableCell className="text-sm">
                      {approver.decidedAt
                        ? formatDisplayDate(approver.decidedAt)
                        : t('common:table.noValue')}
                    </TableCell>
                  </TableRow>
                );
              })}

            {/* Create: picked approvers — name + (optional) suggested badge + remove */}
            {isEditable &&
              editableApprovers?.map(approver => (
                <TableRow key={approver.id} className="hover:bg-transparent">
                  <TableCell>
                    <ApproverNameCell
                      name={approver.name}
                      profileImageUrl={approver.profileImageUrl}
                    />
                  </TableCell>
                  {showInfoColumn && (
                    <TableCell>
                      {approver.isSuggested && (
                        <Badge className="border-transparent bg-blue-500/15 text-blue-600 hover:bg-blue-500/15 dark:text-blue-400">
                          {t('details.approvers.suggested')}
                        </Badge>
                      )}
                    </TableCell>
                  )}
                  <TableCell className="w-16 text-right">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      aria-label={t('details.approvers.remove')}
                      onClick={() => onRemoveApprover?.(approver.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}

            {renderAddRow()}
          </TableBody>
        )}
      </Table>
    </div>
  );
};
